import type {
  SettingsInvite,
  SettingsNotification,
  SettingsUser,
} from '@workspace/seedforge-ui/settings'
import { SettingsView } from '@workspace/seedforge-ui/settings'
import { InvestorLayout } from '../../components/investor-layout'
import { useInvestorContext } from '../../contexts/investor-guard'
import { DealFlowPreferencesSection } from '../../features/deal-flow/components/deal-flow-preferences-section'
import { useMcpSettingsQuery } from '../../features/investor-assistant/hooks/use-investor-assistant'
import {
  useCancelInvestorInviteMutation,
  useCreateInvestorInviteMutation,
  useInvestorInvitesQuery,
} from '../../hooks/api'

function getInitials(name: string | null, email: string | null): string {
  if (name) {
    return name
      .split(' ')
      .map((w) => w[0])
      .join('')
      .toUpperCase()
      .slice(0, 2)
  }
  return email?.charAt(0).toUpperCase() ?? '?'
}

const INVESTOR_NOTIFICATIONS: SettingsNotification[] = [
  {
    label: 'New startup matched',
    description: 'Get notified when a startup matches your investment thesis',
    enabled: true,
  },
  {
    label: 'User joined',
    description: 'Get notified when someone accepts your invite',
    enabled: true,
  },
  {
    label: 'Weekly summary',
    description: 'A weekly digest of deal flow and activity',
    enabled: false,
  },
]

export function InvestorSettingsPage() {
  const { user, investor } = useInvestorContext()
  const investorId = investor.id
  // The assistant replaces the match-score threshold while it is open for the account.
  const { data: assistant } = useMcpSettingsQuery()
  const assistantEnabled = assistant?.data.assistantEnabled === true
  const { data: invites } = useInvestorInvitesQuery(investorId)
  const createInvite = useCreateInvestorInviteMutation(investorId)
  const cancelInvite = useCancelInvestorInviteMutation(investorId)

  const isOwner = investor.role === 'OWNER'

  const users: SettingsUser[] = [
    {
      id: user.id,
      name: user.name ?? user.email ?? 'Unknown',
      email: user.email ?? '',
      initials: getInitials(user.name ?? null, user.email ?? null),
      role: investor.role ?? 'READ',
      userId: user.id,
    },
  ]

  const pendingInvites: SettingsInvite[] = (invites ?? []).map((i) => ({
    id: i.id,
    email: i.email,
    role: i.role,
    token: i.token,
    createdAt: i.createdAt,
  }))

  return (
    <InvestorLayout activeNav="Settings">
      <SettingsView
        users={users}
        invites={pendingInvites}
        notifications={INVESTOR_NOTIFICATIONS}
        onInvite={(email, role) =>
          createInvite.mutate({
            body: { email, role },
            path: { investorId },
          })
        }
        onCancelInvite={(inviteId) =>
          cancelInvite.mutate({
            path: { investorId, inviteId },
          })
        }
        isInviting={createInvite.isPending}
        isOwner={isOwner}
        widthClassName="max-w-3xl"
      />
      {!assistantEnabled && (
        <div className="mx-auto max-w-3xl px-0 pb-10">
          <DealFlowPreferencesSection
            investorId={investorId}
            minPreScreenTotalScore={investor.minPreScreenTotalScore}
            minAiSessionTotalScore={investor.minAiSessionTotalScore}
          />
        </div>
      )}
    </InvestorLayout>
  )
}
